import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { Button, Stack, Typography, useTheme } from '@mui/material'

import type { FallbackProps } from 'react-error-boundary'

import useSignOut from '~/features/auth/hooks/useSignOut'
import { store } from '~/store'
import rem from '~/utils/ui'

/**
 * Router-root error UI (see app/providers/router). Paired with react-error-boundary.
 * Sign out clears Cognito session, then client store, then resets the boundary — navigation
 * after signOut may unmount this tree, so resetErrorBoundary is best-effort.
 */
function ErrorFallback({ resetErrorBoundary }: FallbackProps) {
  const theme = useTheme()
  const { signOut, isLoading: isSignOutLoading } = useSignOut()
  const resetStore = store((s) => s.resetStore)

  const handleSignOut = async () => {
    await signOut()
    resetStore()
    resetErrorBoundary()
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        width: '100vw',
        minHeight: '100vh',
        height: '100vh',
        px: rem(24),
        backgroundColor: theme.palette.mainBackground,
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: theme.palette.common.white,
          border: 1,
          borderColor: theme.palette.divider,
          borderRadius: rem(16),
          maxWidth: rem(560),
          width: '100%',
          py: rem(48),
          px: rem(24),
          textAlign: 'center',
          gap: rem(12),
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: rem(48),
            height: rem(48),
            borderRadius: '50%',
            backgroundColor: theme.palette.mainBackground,
          }}
        >
          <ErrorOutlineRoundedIcon
            sx={{ fontSize: rem(28), color: theme.palette.error.light }}
          />
        </Stack>

        <Typography variant="h5" color={theme.palette.mainFont}>
          Something went wrong
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          The application hit an unexpected error. You can try again or sign out
          and sign back in.
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          useFlexGap
          sx={{ mt: rem(8) }}
        >
          <Button variant="primaryModern" onClick={resetErrorBoundary}>
            Try again
          </Button>
          <Button
            variant="tertiary1SecondaryModern"
            disabled={isSignOutLoading}
            onClick={async () => {
              await handleSignOut()
            }}
          >
            Sign out
          </Button>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default ErrorFallback
